/**
 * Human-to-Human Chat Service
 * API service for the chat system between students and guides
 * Based on FRONTEND_CHAT_API_GUIDE.md
 */

/**
 * Get API base URL from environment
 */
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // Fallback for development
  return 'http://localhost:8000';
};

const API_BASE = getApiUrl() + '/api/chat';
console.log('💬 Chat API URL:', API_BASE);

/**
 * Get auth headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('userToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Handle API response and errors
 */
const handleResponse = async (response) => {
  // Check if response is JSON
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    // API endpoint doesn't exist yet (returning HTML 404)
    console.warn('Chat API not available - endpoint returned non-JSON response');
    throw new Error('Chat API not available');
  }

  const data = await response.json();

  if (!response.ok) {
    if (response.status === 401) {
      // Token expired - dispatch logout event
      window.dispatchEvent(new CustomEvent('auth:expired'));
      throw new Error('Session expired. Please log in again.');
    }
    if (response.status === 403) {
      // 403 can mean: not a group member, or trying student-to-student chat
      throw new Error(data.message || 'Access denied. You may not be a member of this group or this chat is not allowed.');
    }
    if (response.status === 429) {
      throw new Error('Too many messages. Please wait before sending more.');
    }
    throw new Error(data.message || 'An error occurred');
  }

  return data;
};

const humanChatService = {
  // ==========================================
  // GUIDE MANAGEMENT
  // ==========================================

  /**
   * Get all guides in a group (includes admins and owners)
   * @param {string} groupId - The group ID
   * @returns {Promise<{success: boolean, guides: Array, count: number}>}
   */
  async getGuides(groupId) {
    const response = await fetch(`${API_BASE}/${groupId}/guides`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  /**
   * Assign guide role to a group member (Admin/Owner only)
   * @param {string} groupId - The group ID
   * @param {string} userId - User ID to promote to guide
   * @returns {Promise<{success: boolean, message: string, guide: Object}>}
   */
  async assignGuide(groupId, userId) {
    const response = await fetch(`${API_BASE}/${groupId}/guides`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ userId })
    });
    return handleResponse(response);
  },

  /**
   * Remove guide role from a user (Admin/Owner only)
   * @param {string} groupId - The group ID
   * @param {string} userId - User ID to demote
   * @returns {Promise<{success: boolean, message: string, user: Object}>}
   */
  async removeGuide(groupId, userId) {
    const response = await fetch(`${API_BASE}/${groupId}/guides/${userId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // ==========================================
  // CONVERSATIONS
  // ==========================================

  /**
   * Get user's conversations in a group
   * @param {string} groupId - The group ID
   * @param {Object} options - Query options
   * @param {string} options.type - Filter by 'group' or 'bilateral'
   * @param {number} options.limit - Max results (default 50)
   * @param {number} options.offset - Pagination offset
   * @returns {Promise<{success: boolean, conversations: Array, count: number, total: number, hasMore: boolean}>}
   */
  async getConversations(groupId, options = {}) {
    const params = new URLSearchParams();
    if (options.type) params.set('type', options.type);
    if (options.limit) params.set('limit', String(options.limit));
    if (options.offset) params.set('offset', String(options.offset));

    const url = `${API_BASE}/${groupId}/conversations${params.toString() ? '?' + params : ''}`;
    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  /**
   * Create a bilateral (1-on-1) conversation with another user
   * Note: At least one participant must be a guide/admin/owner
   * @param {string} groupId - The group ID
   * @param {string} participantId - User ID to start conversation with
   * @returns {Promise<{success: boolean, message: string, conversation: Object}>}
   */
  async createConversation(groupId, participantId) {
    const response = await fetch(`${API_BASE}/${groupId}/conversations`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ participantId })
    });
    return handleResponse(response);
  },

  /**
   * Get details of a specific conversation
   * @param {string} conversationId - The conversation ID
   * @returns {Promise<{success: boolean, conversation: Object}>}
   */
  async getConversation(conversationId) {
    const response = await fetch(`${API_BASE}/conversations/${conversationId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // ==========================================
  // MESSAGES
  // ==========================================

  /**
   * Get messages from a conversation
   * @param {string} conversationId - The conversation ID
   * @param {Object} options - Query options
   * @param {number} options.limit - Max messages (default 50)
   * @param {string} options.before - Message ID for older messages
   * @param {string} options.after - Message ID for newer messages
   * @returns {Promise<{success: boolean, messages: Array, count: number, total: number, hasMore: boolean}>}
   */
  async getMessages(conversationId, options = {}) {
    const params = new URLSearchParams();
    if (options.limit) params.set('limit', String(options.limit));
    if (options.before) params.set('before', options.before);
    if (options.after) params.set('after', options.after);

    const url = `${API_BASE}/conversations/${conversationId}/messages${params.toString() ? '?' + params : ''}`;
    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  /**
   * Send a message to a conversation
   * Rate limited: 30 messages per minute
   * @param {string} conversationId - The conversation ID
   * @param {string} content - Message content
   * @returns {Promise<{success: boolean, message: Object}>}
   */
  async sendMessage(conversationId, content) {
    const response = await fetch(`${API_BASE}/conversations/${conversationId}/messages`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ content })
    });
    return handleResponse(response);
  },

  // ==========================================
  // READ RECEIPTS
  // ==========================================

  /**
   * Mark a single message as read
   * @param {string} messageId - The message ID
   * @returns {Promise<{success: boolean}>}
   */
  async markMessageAsRead(messageId) {
    const response = await fetch(`${API_BASE}/messages/${messageId}/read`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  /**
   * Mark all messages in a conversation as read
   * @param {string} conversationId - The conversation ID
   * @returns {Promise<{success: boolean, markedCount: number}>}
   */
  async markAllRead(conversationId) {
    const response = await fetch(`${API_BASE}/conversations/${conversationId}/read`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  /**
   * Get unread count for a conversation
   * @param {string} conversationId - The conversation ID
   * @returns {Promise<{success: boolean, unreadCount: number}>}
   */
  async getUnreadCount(conversationId) {
    const response = await fetch(`${API_BASE}/conversations/${conversationId}/unread`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // ==========================================
  // GROUP CONVERSATION
  // ==========================================

  /**
   * Get or create the main group conversation (triggers backend self-healing)
   * @param {string} groupId - The group ID
   * @returns {Promise<{success: boolean, conversation: Object}>}
   */
  async getGroupConversation(groupId) {
    const response = await fetch(`${API_BASE}/${groupId}/group-conversation`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // ==========================================
  // UTILITY METHODS
  // ==========================================

  /**
   * Get total unread count across all conversations in a group
   * @param {string} groupId - The group ID
   * @returns {Promise<number>} Total unread messages
   */
  async getTotalUnreadCount(groupId) {
    try {
      const data = await this.getConversations(groupId);
      if (data.success && data.conversations) {
        return data.conversations.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);
      }
      return 0;
    } catch (error) {
      console.error('Failed to get total unread count:', error);
      return 0;
    }
  },

  /**
   * Find or create a conversation with a specific user
   * @param {string} groupId - The group ID
   * @param {string} participantId - User ID to chat with
   * @returns {Promise<Object>} The conversation object
   */
  async findOrCreateConversation(groupId, participantId) {
    // createConversation is idempotent - returns existing if found
    const data = await this.createConversation(groupId, participantId);
    return data.conversation;
  },

  /**
   * Get the other participant in a bilateral conversation
   * @param {Object} conversation - The conversation object
   * @param {string} currentUserId - Current user's ID
   * @returns {Object|null} The other participant
   */
  getOtherParticipant(conversation, currentUserId) {
    if (!conversation || !conversation.participants) return null;
    return conversation.participants.find(p => p._id !== currentUserId) || null;
  },

  /**
   * Format a conversation for display
   * @param {Object} conversation - The conversation object
   * @param {string} currentUserId - Current user's ID
   * @returns {Object} Formatted conversation data
   */
  formatConversation(conversation, currentUserId) {
    const isGroup = conversation.type === 'group';
    const otherParticipant = isGroup ? null : this.getOtherParticipant(conversation, currentUserId);

    return {
      id: conversation._id,
      type: conversation.type,
      name: isGroup
        ? (conversation.name || 'Group Chat')
        : (otherParticipant?.nickname || otherParticipant?.username || 'Unknown'),
      avatar: isGroup ? null : otherParticipant?.avatar,
      lastMessage: conversation.lastMessage?.content || '',
      lastMessageTime: conversation.lastMessageAt,
      unreadCount: conversation.unreadCount || 0,
      participants: conversation.participants,
      isActive: conversation.isActive
    };
  }
};

export default humanChatService;
